import type { SmartCreationDraft } from "@adpilot/shared";
import { IconCalendar } from "@tabler/icons-react";
import { format, parseISO } from "date-fns";
import { enUS, zhCN } from "date-fns/locale";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { reviewScheduleToday } from "../review-schedule";

export function PromotionWindow({
  draft,
  validationRequested,
  onDraftChange,
}: {
  draft: SmartCreationDraft;
  validationRequested: boolean;
  onDraftChange: (draft: SmartCreationDraft) => void;
}) {
  const { t, i18n } = useTranslation("smartCreation");
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(() => ({
    from: draft.promotionDateRange?.startDate
      ? parseISO(draft.promotionDateRange.startDate)
      : undefined,
    to: draft.promotionDateRange?.endDate ? parseISO(draft.promotionDateRange.endDate) : undefined,
  }));
  const [touched, setTouched] = useState(false);
  // Match the server and Review validation, which use the UTC calendar day.
  const today = reviewScheduleToday(new Date());
  const startDate = range?.from ? format(range.from, "yyyy-MM-dd") : "";
  const endDate = range?.to ? format(range.to, "yyyy-MM-dd") : "";
  const errorKey =
    !startDate || !endDate
      ? "requiredError"
      : startDate > endDate
        ? "rangeError"
        : endDate < today
          ? "expiredError"
          : null;
  const showError = errorKey !== null && (touched || validationRequested);
  const locale = i18n.resolvedLanguage === "zh-CN" ? zhCN : enUS;
  const displayFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const label =
    range?.from && range.to
      ? `${displayFormat.format(range.from)} – ${displayFormat.format(range.to)}`
      : t("objectives.promotionWindow.placeholder");
  const commit = (nextRange: DateRange | undefined) => {
    const nextStart = nextRange?.from ? format(nextRange.from, "yyyy-MM-dd") : "";
    const nextEnd = nextRange?.to ? format(nextRange.to, "yyyy-MM-dd") : "";
    const promotionDateRange =
      nextStart && nextEnd ? { startDate: nextStart, endDate: nextEnd } : null;
    onDraftChange({
      ...draft,
      promotionDateRange,
      ...(promotionDateRange ? { startDate: nextStart, endDate: nextEnd } : {}),
    });
  };

  return (
    <Field
      data-invalid={showError || undefined}
      className={cn(
        "rounded-md border border-line bg-surface p-3.5",
        showError && "border-destructive bg-destructive/5",
      )}
    >
      <FieldLabel>{t("objectives.promotionWindow.label")}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id="smart-creation-promotion-range"
              type="button"
              variant="outline"
              aria-invalid={showError || undefined}
              className="w-full justify-start font-normal"
            />
          }
        >
          <IconCalendar data-icon="inline-start" />
          <span className={cn("truncate", !range?.to && "text-muted-foreground")}>{label}</span>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={8} className="w-auto p-0">
          <Calendar
            mode="range"
            selected={range}
            onSelect={(_nextRange, day, modifiers) => {
              if (modifiers.disabled) return;
              setTouched(true);
              const nextRange =
                !range?.from || range.to
                  ? { from: day, to: undefined }
                  : day < range.from
                    ? { from: day, to: range.from }
                    : { from: range.from, to: day };
              setRange(nextRange);
              commit(nextRange);
              if (nextRange?.from && nextRange.to) setOpen(false);
            }}
            numberOfMonths={2}
            defaultMonth={range?.from ?? parseISO(today)}
            disabled={{ before: parseISO(today) }}
            locale={locale}
            className="p-3 [--cell-size:--spacing(8)]"
          />
        </PopoverContent>
      </Popover>
      {showError && errorKey ? (
        <FieldError match={true}>{t(`objectives.promotionWindow.${errorKey}`)}</FieldError>
      ) : (
        <FieldDescription>{t("objectives.promotionWindow.hint")}</FieldDescription>
      )}
    </Field>
  );
}
